import React, { useEffect, useState, useCallback } from 'react';
import { Star, ExternalLink, Clock, CheckCircle, Lock, Snowflake } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

function formatCooldown(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function VoteCard({ site, onVoteSubmit }) {
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(site.cooldown_remaining_seconds);
  const [canVote, setCanVote] = useState(site.can_vote);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cooldown <= 0) { setCanVote(true); return; }
    const interval = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); setCanVote(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const handleVote = async () => {
    window.open(site.vote_url, '_blank', 'noopener,noreferrer');
    if (site.callback_type === 'manual') {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/votes/submit`,
          { site_id: site.site_id },
          { withCredentials: true }
        );
        setSuccess(data.crystals_awarded);
        setCanVote(false);
        setCooldown(site.cooldown_hours * 3600);
        if (window.gtag) {
          window.gtag('event', 'vote_submitted', {
            site: site.site_id,
            crystals_earned: data.crystals_awarded,
          });
        }
        if (onVoteSubmit) onVoteSubmit(data.frost_crystals);
      } catch (err) {
        setError(err.response?.data?.detail || 'Error al registrar el voto');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div
      className="flex flex-col rounded-xl overflow-hidden transition-transform duration-200 hover:-translate-y-1"
      style={{
        background: 'rgba(10,15,30,0.88)',
        border: '1px solid rgba(0,180,255,0.25)',
        boxShadow: '0 0 28px rgba(0,120,200,0.12), 0 4px 20px rgba(0,0,0,0.5)',
      }}
    >
      <div className="flex flex-col items-center justify-center gap-2 py-6"
        style={{ background: 'rgba(0,0,0,0.25)' }}>
        <Star size={36} className="text-yellow-400" fill="currentColor" />
        <span className="text-xs font-bold tracking-widest text-cyan-400">
          VOTAR
        </span>
      </div>

      <div className="flex flex-col flex-1 p-5 gap-4">
        <div>
          <h3 className="text-white font-bold text-base leading-tight">{site.name}</h3>
          <p className="text-slate-400 text-xs mt-1">
            Cooldown: {site.cooldown_hours}h — Recompensa:{' '}
            <span className="text-cyan-300 font-semibold">+{site.reward_crystals} Crystals</span>
          </p>
        </div>

        {success !== null && (
          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
            style={{ background: 'rgba(30,180,60,0.12)', border: '1px solid rgba(30,180,60,0.3)', color: 'rgba(80,220,140,0.9)' }}>
            <CheckCircle size={13} />
            <span>+{success} Cristales de Escarcha recibidos</span>
          </div>
        )}

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg"
            style={{ background: 'rgba(220,50,50,0.1)', border: '1px solid rgba(220,50,50,0.3)', color: 'rgba(255,100,100,0.9)' }}>
            {error}
          </div>
        )}

        <div className="mt-auto pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {canVote ? (
            <>
              <button
                onClick={handleVote}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all hover:brightness-125 active:scale-95"
                style={{ border: '1px solid rgba(0,229,255,0.4)', color: '#fff', background: 'rgba(0,150,220,0.15)' }}
              >
                <ExternalLink size={14} />
                {loading ? 'Registrando...' : `Votar en ${site.name}`}
              </button>
              <p className="text-center text-xs mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {site.callback_type === 'manual'
                  ? 'Se abrirá el sitio y tu voto quedará registrado.'
                  : 'Recibirás los cristales automáticamente tras votar.'}
              </p>
            </>
          ) : (
            <div className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm"
              style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.45)' }}>
              <Clock size={14} />
              <span>Disponible en <strong>{formatCooldown(cooldown)}</strong></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VotarPage() {
  const [sites, setSites] = useState([]);
  const [user, setUser] = useState(undefined);
  const [crystals, setCrystals] = useState(0);
  const [loadingVotes, setLoadingVotes] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(({ data }) => { setUser(data); setCrystals(data.frost_crystals ?? 0); })
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoadingVotes(true);
    axios.get(`${BACKEND_URL}/api/votes/status`, { withCredentials: true })
      .then(({ data }) => setSites(data))
      .catch(() => { })
      .finally(() => setLoadingVotes(false));
  }, [user]);

  const handleVoteSubmit = useCallback((newCrystals) => {
    setCrystals(newCrystals);
  }, []);

  return (
    <PageWrapper scrollable>
      <SEOMeta title="Votar por Frostbound" noIndex />
      <Navbar />

      <main className="shop-main">
        <div className="shop-header anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="shop-title">
            <Star size={30} className="shop-title-icon" fill="currentColor" />
            VOTAR
          </h1>
          <p className="shop-subtitle">
            Apoya el servidor y gana{' '}
            <span className="cyan-highlight">Cristales de Escarcha</span>{' '}
            por cada voto
          </p>

          {user && (
            <div className="flex justify-center mt-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg"
                style={{ background: 'rgba(0,180,255,0.08)', border: '1px solid rgba(0,180,255,0.25)' }}>
                <Snowflake size={15} className="text-cyan-400" />
                <span className="text-sm text-slate-300">Tus Cristales de Escarcha</span>
                <span className="font-bold text-cyan-300">{crystals.toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        {user === null && (
          <div className="mx-auto max-w-sm mb-8 flex flex-col items-center gap-4 p-8 rounded-xl text-center anim-fade-up"
            style={{ background: 'rgba(10,15,30,0.88)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Lock size={24} className="text-slate-400" />
            <p className="text-slate-400 text-sm">
              Necesitas iniciar sesión para votar y recibir recompensas.
            </p>
            <Link
              to="/login"
              className="px-6 py-2 rounded-lg text-sm font-bold transition-all hover:brightness-125"
              style={{ background: 'rgba(0,150,220,0.2)', border: '1px solid rgba(0,229,255,0.4)', color: 'rgba(0,229,255,0.95)' }}
            >
              Iniciar Sesión
            </Link>
          </div>
        )}

        {user && (
          <div className="shop-grid anim-fade-up" style={{ animationDelay: '0.2s', maxWidth: '720px', margin: '0 auto 40px' }}>
            {loadingVotes ? (
              <p className="text-center text-slate-500 py-16 col-span-full text-sm">
                Cargando sitios de votación...
              </p>
            ) : (
              sites.map(site => (
                <VoteCard key={site.site_id} site={site} onVoteSubmit={handleVoteSubmit} />
              ))
            )}
          </div>
        )}

        <div className="mx-auto max-w-2xl mt-4 mb-4 rounded-xl p-5 text-center anim-fade-up"
          style={{ background: 'rgba(0,180,255,0.05)', border: '1px solid rgba(0,180,255,0.15)', animationDelay: '0.3s' }}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Star size={16} className="text-yellow-400" fill="currentColor" />
            <span className="text-white font-bold text-sm">Como funcionan los votos</span>
          </div>
          <p className="text-slate-400 text-sm">
            Vota en cada plataforma y gana <span className="text-cyan-300 font-semibold">50 Cristales de Escarcha</span> por voto.
            TopG permite votar cada <span className="text-cyan-300 font-semibold">6 horas</span> y XTremetop100 cada{' '}
            <span className="text-cyan-300 font-semibold">24 horas</span>.
            Usa los cristales en la <Link to="/tienda" className="text-cyan-400 hover:text-cyan-300">Tienda</Link>.
          </p>
        </div>
      </main>

      <Footer />
    </PageWrapper>
  );
}
